import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// mapeia o status cru do jambonz para o estado visual da chamada
const RINGING = ["queued", "trying", "ringing", "early-media", "early_media"];
const TERMINAL = ["completed", "failed", "busy", "no-answer", "no_answer", "canceled", "cancelled", "rejected"];
const uiState = status => {
  const s = String(status || "").toLowerCase();
  if (s === "in-progress" || s === "in_progress" || s === "answered") return "in_call";
  if (TERMINAL.includes(s)) return "ended";
  if (RINGING.includes(s)) return "ringing";
  return "ringing";
};
const endedLabel = status => {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "Chamada encerrada";
  if (s === "busy") return "Ocupado";
  if (s === "no-answer" || s === "no_answer") return "Não atendeu";
  if (s === "failed") return "Não completou";
  return "Chamada encerrada";
};
// mensagem amigável do porquê a ligação não completou (a partir do motivo SIP)
const failureMessage = (status, sipStatus, sipReason) => {
  const s = String(status || "").toLowerCase();
  if (s === "busy") return "Linha ocupada.";
  if (s === "no-answer" || s === "no_answer") return "Ninguém atendeu a tempo.";
  if (s !== "failed") return null;
  const code = Number(sipStatus);
  if (code === 480 || /unavailable/i.test(sipReason || "")) {
    return "Telefone indisponível — desligado, sem sinal ou em modo avião.";
  }
  if (code === 486 || code === 600) return "Linha ocupada.";
  if (code === 603 || code === 403) return "A pessoa recusou a chamada.";
  if (code === 404 || code === 484) return "Número inválido ou inexistente.";
  if (sipReason) return `Não completou — ${sipReason}${sipStatus ? ` (${sipStatus})` : ""}.`;
  return "Não foi possível completar a chamada.";
};
const fmtDur = ms => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const useStyles = makeStyles(theme => ({
  card: {
    marginLeft: 40,
    marginTop: 4,
    marginBottom: 10,
    maxWidth: 520,
    background: "linear-gradient(180deg,#11161D 0%,#0B0F14 100%)",
    border: "1px solid rgba(255,255,255,.07)",
    borderRadius: 18,
    padding: "20px 20px 16px",
    color: "#E6E9ED",
    fontFamily: FONT,
    boxShadow: "0 8px 30px rgba(0,0,0,.18)",
  },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  statusDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  statusText: { fontSize: 13.5, fontWeight: 600, letterSpacing: "-0.01em" },
  dur: { marginLeft: "auto", fontSize: 12.5, color: "#9aa3ad", fontVariantNumeric: "tabular-nums" },

  stage: { display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0 16px" },
  orbWrap: { position: "relative", width: 92, height: 92, display: "flex", alignItems: "center", justifyContent: "center" },
  orb: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "radial-gradient(circle at 35% 30%, #D7FF4A 0%, #A6E000 45%, #5f8a00 100%)",
    boxShadow: "0 0 28px rgba(206,255,0,.45)",
  },
  ring: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "2px solid rgba(206,255,0,.35)",
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(.7)", opacity: 0.55 },
    "100%": { transform: "scale(1.25)", opacity: 0 },
  },
  "@keyframes breathe": {
    "0%,100%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.08)" },
  },
  orbBreathe: { animation: "$breathe 2.4s ease-in-out infinite" },
  ringPulseSlow: { animation: "$pulse 2s ease-out infinite" },
  ringPulseFast: { animation: "$pulse 1.2s ease-out infinite" },
  orbEnded: { filter: "grayscale(1) brightness(.6)", boxShadow: "none" },
  toLine: { marginTop: 12, fontSize: 13, color: "#aeb5bd" },
  toNum: { color: "#E6E9ED", fontWeight: 600 },

  transcript: {
    borderTop: "1px solid rgba(255,255,255,.07)",
    paddingTop: 12,
    maxHeight: 200,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,.15)", borderRadius: 3 },
  },
  turn: { fontSize: 13.5, lineHeight: 1.5 },
  turnWho: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#7d8590", marginBottom: 1 },
  turnIa: { color: "#E6E9ED" },
  turnUser: { color: "#9fd2ff" },
  empty: { fontSize: 12.5, color: "#6b7480", textAlign: "center", paddingTop: 4 },
  hint: { fontSize: 11.5, color: "#6b7480", textAlign: "center", marginTop: 10 },
  failMsg: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderTop: "1px solid rgba(255,255,255,.07)",
    paddingTop: 14,
    marginTop: 2,
    fontSize: 13.5,
    lineHeight: 1.45,
    color: "#F3D6A6",
  },
  failIcon: {
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "rgba(245,196,81,.16)",
    color: "#F5C451",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
  },
  resultBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    borderTop: "1px solid rgba(255,255,255,.07)",
    paddingTop: 14,
    marginTop: 2,
    fontSize: 14,
    lineHeight: 1.5,
    color: "#E6E9ED",
  },
  resultIcon: { flexShrink: 0, fontSize: 16, fontWeight: 700, lineHeight: "21px", width: 16, textAlign: "center" },
}));

const CallArtifact = ({ to, status, sipStatus, sipReason, result, transcript, answeredAt }) => {
  const classes = useStyles();
  const state = uiState(status);
  const scrollRef = useRef(null);
  const failMsg = state === "ended" ? failureMessage(status, sipStatus, sipReason) : null;
  // resultado estruturado da tarefa (ligação ad-hoc) capturado ao encerrar
  const resText = result && (result.resultado || result.resumo || "");
  const showResult = state === "ended" && !failMsg && resText;

  // cronômetro vivo enquanto em chamada
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (state !== "in_call") return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript]);

  const color = state === "in_call" ? "#CEFF00" : state === "ended" ? "#7d8590" : "#F5C451";
  const label = state === "in_call" ? "Em chamada" : state === "ended" ? endedLabel(status) : "Chamando…";
  const duration = answeredAt ? fmtDur((state === "ended" ? answeredAt.endedAt || now : now) - answeredAt.at) : null;

  return (
    <div className={classes.card}>
      <div className={classes.head}>
        <span className={classes.statusDot} style={{ background: color, boxShadow: state !== "ended" ? `0 0 8px ${color}` : "none" }} />
        <span className={classes.statusText} style={{ color }}>
          {label}
        </span>
        {duration && state !== "ringing" && <span className={classes.dur}>{duration}</span>}
      </div>

      <div className={classes.stage}>
        <div className={classes.orbWrap}>
          {state !== "ended" && (
            <>
              <span className={`${classes.ring} ${state === "in_call" ? classes.ringPulseFast : classes.ringPulseSlow}`} />
              <span className={`${classes.ring} ${state === "in_call" ? classes.ringPulseFast : classes.ringPulseSlow}`} style={{ animationDelay: ".6s" }} />
            </>
          )}
          <span className={`${classes.orb} ${state === "in_call" ? classes.orbBreathe : ""} ${state === "ended" ? classes.orbEnded : ""}`} />
        </div>
        <div className={classes.toLine}>
          Ligando para <span className={classes.toNum}>{to}</span>
        </div>
      </div>

      {failMsg && (
        <div className={classes.failMsg}>
          <span className={classes.failIcon}>!</span>
          <span>{failMsg}</span>
        </div>
      )}

      {!failMsg && (
        <div className={classes.transcript} ref={scrollRef}>
          {(!transcript || transcript.length === 0) && (
            <div className={classes.empty}>
              {state === "ended" ? "Sem transcrição." : "A transcrição aparece aqui em tempo real…"}
            </div>
          )}
          {(transcript || []).map((t, i) => {
            const isUser = String(t.role || "").toLowerCase() === "user";
            return (
              <div key={i} className={classes.turn}>
                <div className={classes.turnWho}>{isUser ? "Pessoa" : "Numera"}</div>
                <div className={isUser ? classes.turnUser : classes.turnIa}>{t.text}</div>
              </div>
            );
          })}
        </div>
      )}
      {showResult && (
        <div className={classes.resultBox}>
          <span className={classes.resultIcon} style={{ color: result.sucesso === false ? "#F5C451" : "#CEFF00" }}>
            {result.sucesso === false ? "!" : "✓"}
          </span>
          <span>{resText}</span>
        </div>
      )}
      {state === "ended" && !failMsg && !showResult && (
        <div className={classes.hint}>Ligação encerrada.</div>
      )}
    </div>
  );
};

export default CallArtifact;
